import React, { useState, useEffect } from 'react';
import { getAuth, updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

const Settings = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const navigate = useNavigate();
    
    const [profileData, setProfileData] = useState({
        displayName: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        birthDate: '',
        birthTime: '',
        birthLocation: '',
    });
    
    const [message, setMessage] = useState({ text: '', isError: false });
    const [activeSection, setActiveSection] = useState('account');
    
    useEffect(() => {
        if (user) {
            setProfileData(prev => ({
                ...prev,
                displayName: user.displayName || '',
                email: user.email || '',
            }));
            
            // Here you would normally fetch additional user data from your database
            // For example, birth information that was collected during signup
        }
    }, [user]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
        setMessage({ text: '', isError: false });
    };
    
    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setMessage({ text: '', isError: false });
        
        try {
            await updateProfile(user, {
                displayName: profileData.displayName
            });
            
            setMessage({ text: 'Profile updated successfully', isError: false });
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({ text: error.message, isError: true });
        }
    };
    
    const handleUpdateEmail = async (e) => {
        e.preventDefault();
        setMessage({ text: '', isError: false });
        
        if (!profileData.currentPassword) {
            setMessage({ text: 'Current password is required to update email', isError: true });
            return;
        }
        
        try {
            // Reauthenticate user
            const credential = EmailAuthProvider.credential(
                user.email,
                profileData.currentPassword
            );
            
            await reauthenticateWithCredential(user, credential);
            
            // Update email
            await updateEmail(user, profileData.email);
            
            setMessage({ text: 'Email updated successfully', isError: false });
            setProfileData(prev => ({ ...prev, currentPassword: '' }));
        } catch (error) {
            console.error('Error updating email:', error);
            setMessage({ text: error.message, isError: true });
        }
    };
    
    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setMessage({ text: '', isError: false });
        
        if (!profileData.currentPassword) {
            setMessage({ text: 'Current password is required', isError: true });
            return;
        }
        
        if (profileData.newPassword !== profileData.confirmPassword) {
            setMessage({ text: 'New passwords do not match', isError: true });
            return;
        }
        
        if (profileData.newPassword.length < 6) {
            setMessage({ text: 'New password must be at least 6 characters', isError: true });
            return;
        }
        
        try {
            // Reauthenticate user
            const credential = EmailAuthProvider.credential(
                user.email,
                profileData.currentPassword
            );
            
            await reauthenticateWithCredential(user, credential);
            
            // Update password
            await updatePassword(user, profileData.newPassword);
            
            setMessage({ text: 'Password updated successfully', isError: false });
            setProfileData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));
        } catch (error) {
            console.error('Error updating password:', error);
            setMessage({ text: error.message, isError: true });
        }
    };
    
    const handleUpdateBirthInfo = async (e) => {
        e.preventDefault();
        setMessage({ text: '', isError: false });
        
        try {
            // Here you would update birth information in your database
            // For example, using Firestore
            
            setMessage({ text: 'Birth information updated successfully', isError: false });
        } catch (error) {
            console.error('Error updating birth information:', error);
            setMessage({ text: error.message, isError: true });
        }
    };
    
    return (
        <div className="settings-container">
            <div className="settings-content">
                <h1 className="settings-title">Settings</h1>
                
                <div className="settings-tabs">
                    <button 
                        className={`tab-button ${activeSection === 'account' ? 'active' : ''}`}
                        onClick={() => setActiveSection('account')}
                    >
                        Account
                    </button>
                    <button 
                        className={`tab-button ${activeSection === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveSection('security')}
                    >
                        Security
                    </button>
                    <button 
                        className={`tab-button ${activeSection === 'preferences' ? 'active' : ''}`}
                        onClick={() => setActiveSection('preferences')}
                    >
                        Preferences
                    </button>
                </div>
                
                {message.text && (
                    <div className={`message ${message.isError ? 'error' : 'success'}`}>
                        {message.text}
                    </div>
                )}
                
                {activeSection === 'account' && (
                    <div className="settings-section">
                        <form onSubmit={handleUpdateProfile}>
                            <div className="form-group">
                                <label htmlFor="displayName">Display Name</label>
                                <input
                                    type="text"
                                    id="displayName"
                                    name="displayName"
                                    className="form-input-signup"
                                    value={profileData.displayName}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <button type="submit" className="form-button">Update Profile</button>
                        </form>
                        
                        <div className="divider">
                            <div className="divider-line"></div>
                        </div>
                        
                        <form onSubmit={handleUpdateEmail}>
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-input-signup"
                                    value={profileData.email}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="currentPassword">Current Password</label>
                                <input
                                    type="password"
                                    id="currentPassword"
                                    name="currentPassword"
                                    className="form-input-signup"
                                    value={profileData.currentPassword}
                                    onChange={handleChange}
                                    placeholder="Enter current password to confirm"
                                />
                            </div>
                            
                            <button type="submit" className="form-button">Update Email</button>
                        </form>
                    </div>
                )}
                
                {activeSection === 'security' && (
                    <div className="settings-section">
                        <form onSubmit={handleUpdatePassword}>
                            <div className="form-group">
                                <label htmlFor="currentPassword">Current Password</label>
                                <input
                                    type="password"
                                    id="currentPassword"
                                    name="currentPassword"
                                    className="form-input-signup"
                                    value={profileData.currentPassword}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="newPassword">New Password</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    className="form-input-signup"
                                    value={profileData.newPassword}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm New Password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    className="form-input-signup"
                                    value={profileData.confirmPassword}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <button type="submit" className="form-button">Update Password</button>
                        </form>
                    </div>
                )}
                
                {activeSection === 'preferences' && (
                    <div className="settings-section">
                        <form onSubmit={handleUpdateBirthInfo}>
                            <div className="form-group">
                                <label htmlFor="birthDate">Date of Birth</label>
                                <input
                                    type="date"
                                    id="birthDate"
                                    name="birthDate"
                                    className="form-input-signup"
                                    value={profileData.birthDate}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="birthTime">Time of Birth</label>
                                <input
                                    type="time"
                                    id="birthTime"
                                    name="birthTime"
                                    className="form-input-signup"
                                    value={profileData.birthTime}
                                    onChange={handleChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="birthLocation">Location of Birth</label>
                                <input
                                    type="text"
                                    id="birthLocation"
                                    name="birthLocation"
                                    className="form-input-signup"
                                    value={profileData.birthLocation}
                                    onChange={handleChange}
                                    placeholder="City, Country"
                                />
                            </div>
                            
                            <button type="submit" className="form-button">Update Information</button>
                        </form>
                        
                        <div className="divider">
                            <div className="divider-line"></div>
                        </div>
                        
                        <div className="sign-out-section">
                            <p className="section-info">Sign out from your account</p>
                            <button 
                                className="form-button signout-button"
                                onClick={async () => {
                                    try {
                                        await signOut(auth);
                                        navigate('/signin');
                                    } catch (error) {
                                        console.error('Error signing out:', error);
                                        setMessage({ text: 'Error signing out. Please try again.', isError: true });
                                    }
                                }}
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;